/* eslint-disable react/prop-types */
import { Fragment, useState } from 'react';
import Modal, { ModalFooter, ModalTransition } from '@atlaskit/modal-dialog';
import { css } from '@compiled/react';
import {
  Heading,
  Eyebrow,
  Divider,
  Button,
  ButtonAppearance,
} from '@atlassian/learning-components';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import Form, { Field } from '@atlaskit/form';
import { Radio } from '@atlaskit/radio';

const sectionMessageWrapper = css({
  marginTop: '16px',
});
const modalContentContainer = css({
  paddingLeft: '40px',
  paddingRight: '40px',
  marginTop: '48px',
});

const radioButtonContainer = css({
  marginTop: '16px',
  marginBottom: '-4px',
});

const radioButtonSpacer = css({
  marginTop: '8px',
  marginBottom: '8px',
});

const eyebrowWrapper = css({
  marginBottom: '8px',
  marginTop: '8px',
});

const modalFooterStyles = css({
  marginRight: '-46px',
});

const radioLabelStyle = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
});

type PrimaryAccountModalProps = {
  linkedEmails: string[];
  currentPrimaryEmail?: string;
  isOpen: boolean;
  onPrimaryAccountSelection: (email: string) => void;
};

const PrimaryAccountModal: React.FC<PrimaryAccountModalProps> = ({
  linkedEmails,
  currentPrimaryEmail,
  isOpen,
  onPrimaryAccountSelection,
}) => {
  const [selectedEmail, setSelectedEmail] = useState(currentPrimaryEmail || '');

  const handleSubmit = () => {
    if (selectedEmail) {
      onPrimaryAccountSelection(selectedEmail);
    }
  };

  return (
    <ModalTransition>
      {isOpen && (
        <Modal width={'624px'}>
          <div css={modalContentContainer}>
            <Heading level={3}>{'Choose a primary account'}</Heading>
            <div css={sectionMessageWrapper}>
              <SectionMessage>
                <p>
                  {
                    'To proceed, select a primary account. Having a primary account ensures you retain access to your profile and achievements, even if you move companies.'
                  }
                </p>
              </SectionMessage>
            </div>

            <Eyebrow size="medium">{'LINKED ACCOUNTS'}</Eyebrow>

            <Form onSubmit={handleSubmit}>
              {({ formProps }) => (
                <form {...formProps}>
                  <div css={radioButtonContainer}>
                    <Field
                      name="primaryAccount"
                      defaultValue={currentPrimaryEmail}
                    >
                      {({ fieldProps }) =>
                        linkedEmails.map((email: string, index: number) => (
                          <Fragment key={email}>
                            <Radio
                              label={<span css={radioLabelStyle}>{email}</span>}
                              value={email}
                              name={fieldProps.name}
                              isChecked={selectedEmail === email}
                              onChange={() => setSelectedEmail(email)}
                              // this is an odd error that occurs sometimes with ADS components. searched slack, and this is the best workaround for now
                              onPointerEnterCapture={undefined}
                              onPointerLeaveCapture={undefined}
                            />
                            {index < linkedEmails.length - 1 && (
                              <div css={eyebrowWrapper}>
                                <Divider
                                  color={token('color.border')}
                                  height={2}
                                />
                              </div>
                            )}
                            <div css={radioButtonSpacer} />
                          </Fragment>
                        ))
                      }
                    </Field>
                  </div>
                  <ModalFooter>
                    <div css={modalFooterStyles}>
                      <Button
                        label={'Save as primary'}
                        appearance={ButtonAppearance.PRIMARY}
                        type="submit"
                        as="button"
                        disabled={!selectedEmail}
                      />
                    </div>
                  </ModalFooter>
                </form>
              )}
            </Form>
          </div>
        </Modal>
      )}
    </ModalTransition>
  );
};

export default PrimaryAccountModal;
